import { createSelector } from "reselect";
import { BRANDS, HERO_COMMERCIAL_CATEGORY, RESOLUTION } from "app/constants";
import sortBy from "lodash/sortBy";
import get from "lodash/get";
import { FIRST_MERCHANDISING_POSITION } from "src/@brand/appConstants";
import { formatPreviewDateToUTCDate, isBetweenDates } from "app/utils/utils";

const getMerchandising = state => state?.merchandising;
const merchandisings = state => state?.merchandising?.list;
const getCurrentMerchandising = state => state?.merchandising?.current;
const getProducts = state => state?.products;
const getResolution = state => state?.resolution;
const getPartner = state => state?.partner;
const getBrand = state => state?.brand?.code;
const getSpotlightUri = (state, spotlightUri = null) => spotlightUri;

export const getActiveHeroCommercialMerchCode = createSelector(
	[getPartner, merchandisings],
	(partner, merchandising = []) => {
		const matchingMerch = merchandising.find(({ displayOptions }) => {
			const { category, marketingCode = [] } = displayOptions || {};
			if (category?.name !== HERO_COMMERCIAL_CATEGORY) {
				return false;
			}

			return marketingCode.length === 0 || marketingCode.includes(partner.code);
		});

		return matchingMerch?.code;
	}
);

export const getMerchPreviewStartDate = createSelector(
	[getMerchandising],
	(merchandising = {}) => {
		return merchandising.previewStartDate;
	}
);

/**
 * On ne veut pas les merch PARRAINAGE si le système de parrainage n'est pas actif
 */
export const getVisibleMerchandisings = createSelector(
	[merchandisings, getPartner, getMerchPreviewStartDate],
	(merchandisings = [], partner = {}, previewStartDate) => {
		// previewStartDate est valorisé par la query param ?preview=YYYYMMDD

		if (previewStartDate) {
			const previewDateTimestamp = formatPreviewDateToUTCDate(previewStartDate);

			return merchandisings.filter(merchandising => {
				const merchEndAtTimestamp = new Date(merchandising.endAt).getTime();
				const merchStartAtTimestamp = new Date(merchandising.startAt).getTime();

				if (
					previewDateTimestamp &&
					(previewDateTimestamp >= merchEndAtTimestamp ||
						previewDateTimestamp < merchStartAtTimestamp)
				) {
					return false;
				}

				if (
					!merchandising.displayOptions?.partnerCode ||
					partner.code === merchandising.displayOptions?.partnerCode
				) {
					if (merchandising.code === "PARRAINAGE") {
						return partner.enableSponsorship;
					}

					if (merchandising.code === "PAIEMENT4X") {
						return partner.code === "AFFR" || partner.code === "EKFR";
					}

					return true;
				}

				return false;
			});
		}

		return merchandisings.filter(merchandising => {
			if (isBetweenDates(merchandising.startAt, merchandising.endAt)) {
				if (
					!merchandising.displayOptions?.partnerCode ||
					partner.code === merchandising.displayOptions?.partnerCode
				) {
					if (merchandising.code === "PARRAINAGE") {
						return partner.enableSponsorship;
					}

					if (merchandising.code === "PAIEMENT4X") {
						return partner.code === "AFFR" || partner.code === "EKFR";
					}

					return true;
				}

				return false;
			}

			return false;
		});
	}
);

/**
 * La listing PP n'affiche uniquement que les merchs non catégorisés
 */
export const getUncategorizedVisibleMerchandisings = createSelector(
	[getVisibleMerchandisings],
	(merchandisings = []) => {
		return merchandisings.filter(merchandising => {
			return !merchandising.displayOptions?.category;
		});
	}
);

/**
 * La listing PP n'affiche uniquement que les merchs non catégorisés
 */
export const getCategorizedVisibleMerchandisings = createSelector(
	[getVisibleMerchandisings],
	(merchandisings = []) => {
		const filteredMerchandisings = merchandisings.filter(merchandising => {
			return merchandising.displayOptions?.category?.name;
		});
		return sortBy(filteredMerchandisings, "startAt");
	}
);

export const getListingMerchandisings = createSelector(
	[getUncategorizedVisibleMerchandisings, getResolution],
	(merchandisings = [], resolution) => {
		return sortBy(
			merchandisings.filter(merch => {
				if (
					(resolution === RESOLUTION.LARGE ||
						resolution === RESOLUTION.XLARGE ||
						resolution === RESOLUTION.MEDIUM) &&
					(!merch.photoLarge || !merch.photoLarge.url || merch.photoLarge.url === "")
				) {
					return false;
				} else if (
					resolution === RESOLUTION.SMALL &&
					(!merch.photoSmall || (!merch.photoSmall.url && merch.photoSmall.url === ""))
				) {
					return false;
				}
				return true;
			}),
			"headerIndex"
		);
	}
);

export const getMerchandisingTopicsFilterValuesFromMerchCode = createSelector(
	[getCurrentMerchandising],
	(merchandising = {}) => {
		let merchandisingTopics = [];

		if (merchandising.merchandisingTopics) {
			merchandisingTopics = merchandising.merchandisingTopics.reduce(
				(filterValues, { filterValue: currentFilterValue }) => {
					return currentFilterValue
						? [...filterValues, currentFilterValue]
						: filterValues;
				},
				[]
			);
		}

		return merchandisingTopics;
	}
);

// TODO temporaire (voir FBR-2686)
export const shouldMerchHideProductPriceBanner = createSelector(
	[getCurrentMerchandising],
	(merchandising = {}) => {
		return shouldHideMerchProductPriceBanner(merchandising);
	}
);

export const shouldHideMerchProductPriceBanner = (merchandising = {}) => {
	return !merchandising?.topicFilterValues?.some(topic =>
		["Circuit", "Autotour", "Croisière", "Combiné"].includes(topic)
	);
};

export const getMerchandisingRange0 = createSelector(
	[getUncategorizedVisibleMerchandisings, getProducts, getResolution, getSpotlightUri],
	(merchandisings = [], products = [], resolution, idSpotlight) => {
		return {
			position: FIRST_MERCHANDISING_POSITION,
			merchList: sortBy(
				merchandisings.filter(merch => {
					if (
						(resolution === RESOLUTION.LARGE ||
							resolution === RESOLUTION.XLARGE ||
							resolution === RESOLUTION.MEDIUM) &&
						(!merch.photoLarge || !merch.photoLarge.url || merch.photoLarge.url === "")
					) {
						return false;
					} else if (
						resolution === RESOLUTION.SMALL &&
						(!merch.photoSmall ||
							(!merch.photoSmall.url && merch.photoSmall.url === ""))
					) {
						return false;
					}

					const hasSpotlightProduct = products.some(product => {
						return product.uri === idSpotlight;
					});

					if (hasSpotlightProduct) {
						return merch.fallbackPosition === FIRST_MERCHANDISING_POSITION;
					}

					return merch.position === FIRST_MERCHANDISING_POSITION;
				}),
				"headerIndex"
			),
		};
	}
);

export const getListingHeaderMerchandisings = createSelector(
	[getMerchandisingRange0, getBrand],
	(merchandisings, brand) => {
		if (brand === BRANDS.EK && get(merchandisings, "merchList.length") >= 3) {
			const minimumSlidesListLength = 6;
			let doubleMerchandisingList = merchandisings.merchList;
			if (merchandisings.merchList.length) {
				const maxIteration = minimumSlidesListLength / merchandisings.merchList.length || 1;
				for (let factor = 1; factor < maxIteration; factor++) {
					doubleMerchandisingList = [
						...doubleMerchandisingList,
						...merchandisings.merchList,
					];
				}
				return { ...merchandisings, merchList: doubleMerchandisingList };
			}
		}
		return merchandisings;
	}
);

export const getFlashsaleProductsOfMerchandising = createSelector(
	[getMerchandising],
	(merchandising = {}) => {
		const catalogProducts = merchandising.catalogProducts || [];
		const allProducts = merchandising.products?.concat(catalogProducts);
		const flashsaleProducts = allProducts?.filter(product => {
			return product.isFlashsale === true;
		});
		return flashsaleProducts;
	}
);

export const getProductsOfMerchandising = createSelector(
	[getMerchandising],
	(merchandising = {}) => {
		const catalogProducts = merchandising.catalogProducts || [];
		const allProducts = merchandising.products?.concat(catalogProducts);
		const nonFlashsaleProducts = allProducts?.filter(product => {
			return product.isFlashsale === false;
		});
		return nonFlashsaleProducts;
	}
);

export const getMerchandisingMedias = createSelector(
	[getCurrentMerchandising],
	(merchandising = {}) => {
		const medias = get(merchandising, "media.articles");
		return medias;
	}
);

export const getMerchandisingDescription = createSelector(
	[getCurrentMerchandising],
	(merchandising = {}) => {
		const mediasTitle = get(merchandising, "media.title");
		const mediasDescription = get(merchandising, "media.description");

		return { title: mediasTitle, description: mediasDescription };
	}
);

export const getHeroCommercialCarouselMerchandisings = createSelector(
	[getPartner, getVisibleMerchandisings],
	(partner = {}, merchandisings = []) => {
		const heroCommercialMerchandisings = merchandisings.filter(({ displayOptions }) => {
			const { category, marketingCode = [] } = displayOptions || {};
			if (category?.name !== HERO_COMMERCIAL_CATEGORY) {
				return false;
			}

			return marketingCode.length === 0 || marketingCode.includes(partner?.code);
		});

		const carouselMerchandisings = heroCommercialMerchandisings.filter(
			merchandising => merchandising.displayOptions?.heroCommercial?.carouselIndex
		);

		return sortBy(
			carouselMerchandisings,
			merchandising => merchandising.displayOptions.heroCommercial.carouselIndex
		)
			.slice(0, 3)
			.map((merchandising, index) => ({
				...merchandising,
				displayOptions: {
					...merchandising.displayOptions,
					heroCommercial: {
						...merchandising.displayOptions?.heroCommercial,
						carouselIndex: index,
					},
				},
			}));
	}
);
